package uz.mint.frontend.components.sections

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H4
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.P
import uz.mint.frontend.EyebrowStyle
import uz.mint.frontend.PageGutter
import uz.mint.frontend.ShellStyle
import uz.mint.frontend.SiteConfig
import uz.mint.frontend.components.widgets.InstagramIcon
import uz.mint.frontend.components.widgets.PhoneIcon
import uz.mint.frontend.components.widgets.TelegramIcon
import uz.mint.frontend.i18n.Locale
import uz.mint.frontend.i18n.SiteStrings
import uz.mint.frontend.i18n.compatiblePhonesFor
import uz.mint.frontend.i18n.installGuideFor
import uz.mint.frontend.toSitePalette
import uz.mint.frontend.alignItems
import uz.mint.frontend.alignSelf
import uz.mint.frontend.flexWrapCss
import uz.mint.frontend.gridTemplateColumnsCss
import uz.mint.frontend.justifyContent
import uz.mint.frontend.marginInlineAuto
import uz.mint.frontend.placeItems
import uz.mint.frontend.widthCss

/** Which help document the footer is showing, if any. */
private enum class HelpDoc { INSTALL, DEVICES }

/**
 * @param locale needed for the "Тарифы" link: the catalogue is a real per-locale route, so the
 *   footer of `/uz/` has to point at `/uz/plans` rather than dropping the visitor into Russian.
 *
 * The help overlays are owned here rather than hoisted to the four callers. Unlike the legal
 * ones, nothing outside this footer opens them, and threading two more callbacks through
 * LandingPage, AllPlansPage, LoginPage and AccountShell would be ceremony with no reader.
 */
@Composable
fun Footer(
    locale: Locale,
    strings: SiteStrings,
) {
    val palette = ColorMode.current.toSitePalette()
    var help by remember { mutableStateOf<HelpDoc?>(null) }

    Div(
        Modifier
            .fillMaxWidth()
            .id(FOOTER_ANCHOR)
            .backgroundColor(palette.band).color(palette.bandFg)
            .toAttrs()
    ) {
        Div(
            ShellStyle.toModifier()
                .padding(top = clamp(40.px, 4.4.vw, 68.px), bottom = 28.px, leftRight = PageGutter)
                .display(DisplayStyle.Grid).gap(40.px)
                .gridTemplateColumnsCss("repeat(auto-fit,minmax(min(100%,200px),1fr))")
                .toAttrs()
        ) {
            Column(Modifier.gap(18.px)) {
                // Always the white lockup: the footer band stays dark in both themes.
                Img(src = "/mint-lockup-white.svg", alt = "mint.uz", attrs = Modifier.height(24.px).toAttrs())
                P(Modifier.fontSize(14.px).opacity(0.66).maxWidth(22.em).toAttrs()) {
                    SpanText(strings.footerAbout)
                }
                // The number reads as a line rather than as a third round icon: an icon says
                // "call us" and hides the one thing worth showing, and this is the copy someone
                // scrolls to the bottom looking for. Tappable, like the strip at the top.
                // Coloured explicitly, like every other link on this band: `UncoloredLinkVariant`
                // resolves to the theme's ink rather than to what the parent set, so in the light
                // theme this number was #0F2620 on the #0F2620 footer — invisible. The columns to
                // the right never had the bug because they were already passing `fg` down.
                Link(
                    path = SiteConfig.PHONE_HREF,
                    modifier = Modifier
                        .color(palette.mint)
                        .display(DisplayStyle.Flex).alignItems("center").gap(9.px)
                        .fontSize(17.px).fontWeight(FontWeight.Bold)
                        .letterSpacing((-0.01).em).whiteSpace(WhiteSpace.NoWrap),
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                ) {
                    PhoneIcon(size = 17, color = palette.mint)
                    SpanText(SiteConfig.PHONE)
                }

                Row(Modifier.gap(10.px)) {
                    SocialLink(SiteConfig.TELEGRAM_URL, palette.bandBorder) {
                        TelegramIcon(color = palette.bandFg)
                    }
                    SocialLink(SiteConfig.INSTAGRAM_URL, palette.bandBorder) {
                        InstagramIcon(color = palette.bandFg)
                    }
                }
            }

            FooterColumn(
                strings.footerC1,
                listOf(
                    FooterLink.Goto(strings.footerL1, "#$TOP_ANCHOR"),
                    // The full catalogue route, not the landing anchor: from `/plans` or a
                    // signed-in page the anchor points at a section that is not on the page.
                    FooterLink.Goto(strings.footerL2, locale.plansPath),
                ),
                palette.bandFg,
            )

            FooterColumn(
                strings.footerC2,
                listOf(
                    FooterLink.Action(strings.footerL4) { help = HelpDoc.INSTALL },
                    FooterLink.Action(strings.footerL5) { help = HelpDoc.DEVICES },
                    // Was a Telegram link: it sent a visitor off the site at the moment they
                    // were deciding whether to buy, and answered nothing until someone replied.
                    FooterLink.Goto(strings.footerL6, locale.faqPath),
                ),
                palette.bandFg,
            )

            Column(Modifier.gap(14.px)) {
                H4(EyebrowStyle.toModifier().fontSize(11.px).letterSpacing(0.12.em).opacity(0.55).toAttrs()) {
                    SpanText(strings.footerC3)
                }
                Row(Modifier.gap(8.px).flexWrapCss()) {
                    PAYMENT_BRANDS.forEach { (name, asset) ->
                        PaymentBadge(name, asset)
                    }
                }
            }
        }

        Row(
            ShellStyle.toModifier()
                .padding(top = 20.px, bottom = 34.px, leftRight = PageGutter)
                .borderTop(2.px, LineStyle.Solid, palette.bandLine)
                .gap(16.px).flexWrapCss().fontSize(13.px),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SpanText(strings.footerRights, Modifier.opacity(0.55))
            Spacer()
            Row(Modifier.gap(18.px).flexWrapCss()) {
                LegalLink(strings.footerTerms, locale.offerPath, palette.bandFg)
                LegalLink(strings.footerPrivacy, locale.privacyPath, palette.bandFg)
            }
        }
    }

    // Rendered outside the footer band so the overlay is not clipped by it, and reusing the
    // legal reader: same shape of content, so a second overlay component would be a copy.
    when (help) {
        HelpDoc.INSTALL ->
            LegalOverlay(strings.footerL4, installGuideFor(locale), strings) { help = null }

        HelpDoc.DEVICES ->
            LegalOverlay(strings.footerL5, compatiblePhonesFor(locale), strings) { help = null }

        null -> Unit
    }
}

/**
 * A footer entry either navigates or opens something in place.
 *
 * Modelled rather than passed as a nullable href because the two need different elements: an
 * [Action] rendered as `<a href="#">` pushes a bogus history entry and announces itself to a
 * screen reader as a link to nowhere — the same reason the legal links below are buttons.
 */
private sealed interface FooterLink {
    val label: String

    data class Goto(override val label: String, val href: String) : FooterLink
    data class Action(override val label: String, val onClick: () -> Unit) : FooterLink
}

@Composable
private fun FooterColumn(heading: String, links: List<FooterLink>, fg: Color) {
    Column(Modifier.gap(12.px)) {
        H4(EyebrowStyle.toModifier().fontSize(11.px).letterSpacing(0.12.em).opacity(0.55).toAttrs()) {
            SpanText(heading)
        }
        links.forEach { link ->
            when (link) {
                is FooterLink.Goto -> Link(
                    path = link.href,
                    modifier = Modifier.color(fg).fontSize(15.px).fontWeight(FontWeight.Medium),
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                ) { SpanText(link.label) }

                // A button, so it announces itself as one and takes a keyboard — but the
                // browser's default chrome has to be undone explicitly. `border(0.px)` is the
                // one that shows: without it the UA's 2px outset border draws a pale box around
                // the label, which against the dark footer band reads as a stray white frame.
                is FooterLink.Action -> Button(
                    attrs = Modifier
                        .color(fg).fontSize(15.px).fontWeight(FontWeight.Medium)
                        .backgroundColor(Colors.Transparent)
                        .border(0.px)
                        .padding(0.px)
                        .textAlign(TextAlign.Start)
                        .cursor(Cursor.Pointer)
                        .onClick { link.onClick() }
                        .toAttrs()
                ) { SpanText(link.label) }
            }
        }
    }
}

/**
 * What a customer can actually pay with, in the order they meet it: Click is the checkout, Payme
 * sits behind it, and the card schemes are what funds either.
 *
 * Real marks rather than the design's typeset words — those were labelled placeholders in the
 * design itself, and a payment badge a visitor does not recognise does not do the one job a
 * payment badge has.
 */
private val PAYMENT_BRANDS = listOf(
    "Click" to "/pay/click.png",
    "Payme" to "/pay/payme.png",
    // Visa and Mastercard were here, and Uzcard and Humo were named in the offer, all inherited
    // from the design. Removed on the owner's instruction (2026-08-04): we integrate with Click
    // and Payme, and a card mark in the footer promises a checkout this site does not have.
    // Which cards those two accept is their cashier's business, shown on their own page.
)

/**
 * Every one of these marks is drawn for a light background, and the footer band is dark in both
 * colour modes — so each sits on its own white chip rather than being dropped onto the band,
 * where the dark-inked ones (Click, Payme) would simply disappear.
 */
@Composable
private fun PaymentBadge(name: String, asset: String) {
    Div(
        Modifier
            .backgroundColor(Colors.White)
            .borderRadius(8.px)
            .padding(topBottom = 8.px, leftRight = 12.px)
            .height(38.px)
            .display(DisplayStyle.Grid)
            .placeItems("center")
            .toAttrs()
    ) {
        Img(
            src = asset,
            alt = name,
            attrs = Modifier.height(22.px).styleModifier { property("object-fit", "contain") }
                .toAttrs()
        )
    }
}

@Composable
private fun SocialLink(href: String, border: Color, icon: @Composable () -> Unit) {
    Link(
        path = href,
        modifier = Modifier
            .size(38.px)
            .border(2.px, LineStyle.Solid, border)
            .display(DisplayStyle.Grid)
            .justifyContent("center")
            .alignItems("center"),
        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
    ) { icon() }
}

/**
 * A legal document link.
 *
 * These were plain text until 2026-08-04, while the documents still carried a fabricated taxpayer
 * number and promised refunds that did not exist — a label was the honest shape for something
 * unpublishable. Both are settled and client-approved now, so they are pressable again.
 */
@Composable
/**
 * @param fg the band's foreground, passed in rather than inherited.
 *
 * These two links were invisible in the light theme for the same reason the phone above was: an
 * "uncolored" link takes the theme's ink, not its parent's colour, and the footer band is dark in
 * both. The offer and the privacy policy are the two documents a customer is entitled to find, so
 * this is the one place on the site where a colour bug was also a compliance one.
 */
private fun LegalLink(label: String, path: String, fg: Color) {
    Link(
        path = path,
        modifier = Modifier.color(fg).opacity(0.75).fontSize(13.px)
            .textDecorationLine(TextDecorationLine.Underline),
        variant = UncoloredLinkVariant,
    ) { SpanText(label) }
}
